import React, { useEffect } from 'react'
import styled, { css } from 'styled-components'

import FilterPanel, { IFilterBundle, IFilterDescriptor } from './FilterPanel'
import { FilterChipDescriptor } from './FilterChips'
import { shadows } from '../../styles/effects'
import { useStickyNavOffset } from '../../hooks/useStickyNavOffset'

interface IProps {
  filters: IFilterBundle
  prependCustom?: React.ReactNode
  prependFilters?: IFilterDescriptor[]
  extraFilters?: IFilterDescriptor[]
  extraChips?: FilterChipDescriptor[]
  extraHasActive?: boolean
  onExtraClear?: () => void
  searchTerm?: string
  setSearchTerm?: React.Dispatch<React.SetStateAction<string>>
  searchPlaceholder?: string
  countLabel?: string
  actions?: React.ReactNode
  leadingActions?: React.ReactNode
}

const SENTINEL_ID = 'stickyFilterSentinel'

// How much of the screen the sticky bar takes below the nav, for code that scrolls to an
// element and must not land it under the bar. 0 on a page without one.
export const getStickyFilterHeight = (): number =>
  (document.getElementById(SENTINEL_ID)
    ?.nextElementSibling as HTMLElement | null)?.offsetHeight ?? 0

const StickyFilterPanel: React.FC<IProps> = (props) => {
  const { sentinelRef, navOffset, isStuck } = useStickyNavOffset()

  // Publishes where the panel ends, so a table header can stick right under it
  useEffect(() => {
    const panel = sentinelRef.current?.nextElementSibling as HTMLElement | null
    if (!panel || typeof ResizeObserver === 'undefined') return undefined
    const root = document.documentElement.style
    const publish = () =>
      root.setProperty('--sticky-top', `${navOffset + panel.offsetHeight}px`)
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(panel)
    return () => {
      observer.disconnect()
      root.removeProperty('--sticky-top')
    }
  }, [navOffset])

  const hasChips =
    props.filters.chips.length > 0 || (props.extraChips?.length ?? 0) > 0

  return (
    <>
      <div ref={sentinelRef} id={SENTINEL_ID} />
      <StyledFilterPanel
        {...props}
        navOffset={navOffset}
        isStuck={isStuck}
        hasChips={hasChips}
        hasCountLabel={!!props.countLabel}
      />
    </>
  )
}

export default StickyFilterPanel

const StyledFilterPanel = styled(FilterPanel)<{
  navOffset: number
  isStuck: boolean
  hasChips: boolean
  hasCountLabel: boolean
}>`
  margin-top: 0;
  ${({ navOffset, isStuck, hasChips, hasCountLabel, theme }) => css`
    position: sticky;
    top: ${navOffset}px;
    z-index: 50;
    // Full-bleed, while the padding puts the content back on the edges of the
    // centred column it sits in, whatever that column's width.
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    padding: 12px calc(50vw - 50%);
    background-color: ${theme.colors.primaryLight};

    ${isStuck &&
    css`
      border-bottom: 1px solid ${theme.colors.textLightest};
      box-shadow: ${shadows.depthTwo};
    `}

    > div:first-child {
      align-items: center;
    }

    // The "Filters" label above the buttons, and the count before the chips. Without a
    // count the first span in the chip row is a chip, which must stay.
    > div:first-child > div > span:first-child {
      display: none;
    }

    ${hasCountLabel &&
    css`
      > div:nth-child(2) > div > span:first-child {
        display: none;
      }
    `}

    ${!hasChips &&
    css`
      > *:nth-child(2) {
        display: none;
      }
    `}
  `}
`
